import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { CustomDateInputComponent } from '../../../../shared/components/custom-date-input/custom-date-input.component';
import { CustomNumberInputComponent } from '../../../../shared/components/custom-number-input/custom-number-input.component';
import { LabeledInputComponent } from '../../../../shared/components/labeled-input/labeled-input.component';
import { CustomDropdownComponent } from '../../../../shared/components/custom-dropdown/custom-dropdown.component';
import { DialogModule } from "primeng/dialog";
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { CommodityType, FileType, InvoiceGasData, InvoiceGasDataItem, InvoiceGasExtractedData, PriceType } from '../../../../core/interfaces/invoice-interfaces';
import { AdminService } from '../../../../core/api/admin.service';
import { JSONGeneratorHistoryGasCreate, JSONGeneratorHistoryGasGet } from '../../../../core/interfaces/admin-interfaces';
import { lastValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';

@Component({
  selector: 'app-generate-gas-jsondialog',
  standalone: true,
  imports: [CommonModule, TranslocoPipe, CustomButtonComponent, CustomDateInputComponent, CustomNumberInputComponent, LabeledInputComponent, CustomDropdownComponent, DialogModule],
  templateUrl: './generate-gas-jsondialog.component.html',
  styleUrl: './generate-gas-jsondialog.component.css'
})
export class GenerateGasJSONDialogComponent {
  constructor(private dateService: DateConversionService, private adminService: AdminService, private apiError: ApiErrorService) { }
  @Input() toast!: ToastComponent;
  @Input() reloadCallbackFunction!: () => void;

  visible: boolean = false;

  saveClicked: boolean = false;
  successful: boolean = false;
  dialogErrorMessage: string | null = null;

  // Dropdowns
  priceTypeDropdownOptions: string[] = [];

  // Record Data
  recordData: JSONGeneratorHistoryGasGet | null = null;

  // Inputs
  invoiceNumberInput: string = '';
  totalConsumptionInput: string = '';
  invoiceAmountActualInput: string = '';
  invoiceAmountGrossInput: string = '';
  forPeriodDate: Date | undefined;
  vatRateInput: string = '';
  vatAmountInput: string = '';
  invoiceIdInput: string = '';
  invoiceFileNameInput: string = '';
  invoiceConsumptionClass: string = '';
  nontaxableAmountInput: string = '';

  streetInput: string = '';
  numberInput: string = '';
  postalCodeInput: string = '';
  cityInput: string = '';
  provinceInput: string = '';
  latitudeInput: string = '';
  longitudeInput: string = '';

  // Errors
  invoiceNumberInputError: string | null = null;
  totalConsumptionInputError: string | null = null;
  invoiceAmountActualInputError: string | null = null;
  invoiceAmountGrossInputError: string | null = null;
  forPeriodInputError: string | null = null;
  vatRateInputError: string | null = null;
  vatAmountInputError: string | null = null;
  invoiceIdError: string = '';
  invoiceConsumptionClassError: string | null = null;
  nontaxableAmountInputError: string | null = null;

  streetInputError: string | null = null;
  numberInputError: string | null = null;
  postalCodeInputError: string | null = null;
  cityInputError: string | null = null;
  provinceInputError: string | null = null;
  latitudeInputError: string | null = null;
  longitudeInputError: string | null = null;

  editedPDRs: {
    spread?: string;
    gas_market_price?: string;
    price_gas_var_actual?: string;
    price_gas_fix?: string;
    hcv_gas?: string;
    taxrate_gas_scale1?: string;
    taxrate_gas_scale2?: string;
    taxrate_gas_scale3?: string;
    taxrate_gas_scale4?: string;

    month_gas_consumption?: string;
    variable_gas_consumption?: string;
    fixed_gas_consumption?: string;
    daily_gas_capacity?: string;

    price_type_gas?: string;
    selectedPriceTypeDropdown?: number | undefined;

    gas_market_index?: string;
    gas_intensity_class?: string;
    gas_meter_class?: string;
    REMI_type_gas?: string;
    area_gas?: string;
    tax_region?: string;

    daily_capacity_factor_gas?: string;
    fixed_charge_gas_actual?: string;
    material_cost_factor?: string;
    material_costs_gas_variable_actual?: string;
    material_costs_gas_fixed_actual?: string;
    fixed_distribution_costs_gas_actual?: string;
    fixed_distribution_costs_gas_calc?: string;
    variable_transportation_fee?: string;
    var_transportation_costs_actual?: string;
    total_var_distribution_costs_actual?: string;

    var_distribution_fee_tier1?: string;
    var_distribution_fee_tier2?: string;
    var_distribution_fee_tier3?: string;
    var_distribution_fee_tier4?: string;
    var_distribution_fee_tier5?: string;
    var_distribution_fee_tier6?: string;
    var_distribution_fee_tier7?: string;
    var_distribution_fee_tier8?: string;

    vat_rate?: string;
    vat_amount_actual?: string;

    tms_gas_charges_actual?: string;

    total_gas_taxes_actual?: string;
    additional_gas_charges?: string;
    total_gas_discount_amount?: string;

    total_taxes?: string;
    fixed_quota?: string;
    forecast_index_value?: string;
    gas_perc_with_var_price?: string;
    exercise_tax_type_gas?: string;
    tax_type_gas?: string;
    pdr_id?: string;

    location: {
      street?: string;
      number?: string;
      postal_code?: string;
      city?: string;
      province?: string;
    }
    supplierData: {
      name?: string;
      vat_id?: string;
      contact_name?: string;
      contact_number?: string;
      email?: string;
      start_of_supply?: Date;
      end_of_supply?: Date;
    };
  }[] = [];

  numberOfPdrs: number = 0;

  openModal(invoice: InvoiceGasData | null = null, recordData: JSONGeneratorHistoryGasGet | null = null): void {
    this.resetDialogData();

    this.visible = true;
    this.successful = false;
    this.saveClicked = false;
    this.populateStaticDropdowns();

    this.recordData = recordData;

    if (invoice != null) {
      const data = invoice?.data as InvoiceGasDataItem;
      this.populateInputsFromInvoice(data, invoice?.id || '', recordData)
      this.numberOfPdrs = data.pdr_data.length;
    }
  }

  closeModal(): void {
    this.visible = false;
  }

  populateStaticDropdowns() {
    this.priceTypeDropdownOptions = [];
    this.priceTypeDropdownOptions = Object.values(PriceType)
  }

  getGasInvoiceDataFromInputs(): InvoiceGasData {
    const invoiceData: InvoiceGasData = {
      id: this.invoiceIdInput,
      processed_at: new Date().toISOString() || '',
      type: CommodityType.gas,
      file_type: FileType.invoice,
      data: {
        location: {
          street: this.streetInput || null,
          number: this.numberInput || null,
          postal_code: this.postalCodeInput || null,
          city: this.cityInput || null,
          province: this.provinceInput || null,
          latitude: parseFloat(this.latitudeInput) ?? null,
          longitude: parseFloat(this.longitudeInput) ?? null
        },
        invoice_data: {
          for_period: this.forPeriodDate && !isNaN(new Date(this.forPeriodDate).getTime())
            ? this.dateService.isoToSimpleDate(new Date(this.forPeriodDate).toISOString())
            : undefined,
          invoice_number: this.invoiceNumberInput.trim(),
          total_consumption: parseFloat(this.totalConsumptionInput),
          invoice_amount_net_actual: parseFloat(this.invoiceAmountActualInput),
          invoice_amount_gross_actual: parseFloat(this.invoiceAmountGrossInput),
          consumption_class: this.invoiceConsumptionClass.trim() || '',
          additional_charges: null,
          total_discount_amount: null,
          vat_rate: parseFloat(this.vatRateInput) ?? null,
          vat_amount_actual: parseFloat(this.vatAmountInput) ?? null,
          nontaxable_amount: parseFloat(this.nontaxableAmountInput) ?? null
        },
        pdr_data: this.editedPDRs.map((pdr, i) => {
          const selectedPriceType = this.priceTypeDropdownOptions[pdr.selectedPriceTypeDropdown!]?.toUpperCase() || null;
          return {
            total_taxes: parseFloat(pdr.total_gas_taxes_actual ?? '') ?? null,

            spread: parseFloat(pdr.spread ?? '') ?? null,
            fixed_quota: parseFloat(pdr.fixed_charge_gas_actual ?? '') ?? null,
            forecast_index_value: pdr.forecast_index_value || null,

            month_gas_consumption: parseFloat(pdr.month_gas_consumption ?? '') ?? null,
            variable_gas_consumption: parseFloat(pdr.variable_gas_consumption ?? '') ?? null,
            fixed_gas_consumption: parseFloat(pdr.fixed_gas_consumption ?? '') ?? null,
            gas_perc_with_var_price: pdr.variable_gas_consumption != null ? parseFloat(pdr.variable_gas_consumption) * 100 : null,
            daily_gas_capacity: parseFloat(pdr.daily_gas_capacity ?? '') ?? null,

            price_type_gas: selectedPriceType ?? null,

            gas_market_index: pdr.gas_market_index ?? null,
            gas_market_price: parseFloat(pdr.gas_market_price ?? '') ?? null,
            price_gas_var_actual: parseFloat(pdr.price_gas_var_actual ?? '') ?? null,
            price_gas_fix: parseFloat(pdr.price_gas_fix ?? '') ?? null,
            hcv_gas: parseFloat(pdr.hcv_gas ?? '') ?? null,

            gas_intensity_class: pdr.gas_intensity_class || null,
            gas_meter_class: pdr.gas_meter_class || null,
            REMI_type_gas: pdr.REMI_type_gas || null,
            area_gas: pdr.area_gas || null,
            tax_region: pdr.tax_region ?? null,

            daily_capacity_factor_gas: parseFloat(pdr.daily_capacity_factor_gas ?? '') ?? null,
            fixed_charge_gas_actual: parseFloat(pdr.fixed_charge_gas_actual ?? '') ?? null,

            material_cost_factor: parseFloat(pdr.material_cost_factor ?? '') ?? null,
            material_costs_gas_variable_actual: parseFloat(pdr.material_costs_gas_variable_actual ?? '') ?? null,
            material_costs_gas_fixed_actual: parseFloat(pdr.material_costs_gas_fixed_actual ?? '') ?? null,

            fixed_distribution_costs_gas_calc: parseFloat(pdr.fixed_distribution_costs_gas_calc ?? '') ?? null,
            fixed_distribution_costs_gas_actual: parseFloat(pdr.fixed_distribution_costs_gas_actual ?? '') ?? null,
            variable_transportation_fee: parseFloat(pdr.variable_transportation_fee ?? '') ?? null,
            var_transportation_costs_actual: parseFloat(pdr.var_transportation_costs_actual ?? '') ?? null,

            total_var_distribution_costs_actual: parseFloat(pdr.total_var_distribution_costs_actual ?? '') ?? null,

            tms_gas_charges_actual: parseFloat(pdr.tms_gas_charges_actual ?? '') ?? null,

            exercise_tax_type_gas: pdr.exercise_tax_type_gas || null,
            tax_type_gas: pdr.tax_type_gas || null,

            taxrate_gas_scale1: parseFloat(pdr.taxrate_gas_scale1 ?? '') ?? null,
            taxrate_gas_scale2: parseFloat(pdr.taxrate_gas_scale2 ?? '') ?? null,
            taxrate_gas_scale3: parseFloat(pdr.taxrate_gas_scale3 ?? '') ?? null,
            taxrate_gas_scale4: parseFloat(pdr.taxrate_gas_scale4 ?? '') ?? null,

            total_gas_taxes_actual: parseFloat(pdr.total_gas_taxes_actual ?? '') ?? null,
            additional_gas_charges: parseFloat(pdr.additional_gas_charges ?? '') ?? null,
            total_gas_discount_amount: parseFloat(pdr.total_gas_discount_amount ?? '') ?? null,

            var_distribution_fee_tier1: parseFloat(pdr.var_distribution_fee_tier1 ?? '') ?? null,
            var_distribution_fee_tier2: parseFloat(pdr.var_distribution_fee_tier2 ?? '') ?? null,
            var_distribution_fee_tier3: parseFloat(pdr.var_distribution_fee_tier3 ?? '') ?? null,
            var_distribution_fee_tier4: parseFloat(pdr.var_distribution_fee_tier4 ?? '') ?? null,
            var_distribution_fee_tier5: parseFloat(pdr.var_distribution_fee_tier5 ?? '') ?? null,
            var_distribution_fee_tier6: parseFloat(pdr.var_distribution_fee_tier6 ?? '') ?? null,
            var_distribution_fee_tier7: parseFloat(pdr.var_distribution_fee_tier7 ?? '') ?? null,
            var_distribution_fee_tier8: parseFloat(pdr.var_distribution_fee_tier8 ?? '') ?? null,

            pdr_id: pdr.pdr_id || null,

            vat_rate: parseFloat(pdr.vat_rate ?? '') ?? null,
            vat_amount_actual: parseFloat(pdr.vat_amount_actual ?? '') ?? null,

            supplier: {
              name: pdr.supplierData.name || null,
              vat_id: pdr.supplierData.vat_id || null,
              contact_number: pdr.supplierData.contact_number || null,
              email: pdr.supplierData.email || null,
              contact_name: pdr.supplierData.contact_name || null,
              start_of_supply: pdr.supplierData.start_of_supply && !isNaN(new Date(pdr.supplierData.start_of_supply).getTime())
                ? this.dateService.isoToSimpleDate(new Date(pdr.supplierData.start_of_supply).toISOString())
                : undefined,
              end_of_supply: pdr.supplierData.end_of_supply && !isNaN(new Date(pdr.supplierData.end_of_supply).getTime())
                ? this.dateService.isoToSimpleDate(new Date(pdr.supplierData.end_of_supply).toISOString())
                : undefined

            },

            location: {
              street: pdr.location.street || null,
              number: pdr.location.number || null,
              postal_code: pdr.location.postal_code || null,
              city: pdr.location.city || null,
              province: pdr.location.province || null
            },
          } as InvoiceGasExtractedData;
        }),
        historical_data: []
      }
    };

    return invoiceData;
  }

  populateInputsFromInvoice(item: InvoiceGasDataItem, id: string, recordData: JSONGeneratorHistoryGasGet | null): void {
    // Base invoice inputs
    this.invoiceIdInput = id;
    this.invoiceFileNameInput = recordData?.file_name || '';
    this.forPeriodDate = item.invoice_data.for_period
      ? new Date(item.invoice_data.for_period)
      : undefined;
    this.invoiceNumberInput = item.invoice_data.invoice_number ?? '';
    this.totalConsumptionInput = item.invoice_data.total_consumption?.toString() ?? '';
    this.invoiceAmountActualInput = item.invoice_data.invoice_amount_net_actual?.toString() ?? '';
    this.invoiceAmountGrossInput = item.invoice_data.invoice_amount_gross_actual?.toString() ?? '';
    this.vatRateInput = item.invoice_data.vat_rate?.toString() ?? '';
    this.vatAmountInput = item.invoice_data.vat_amount_actual?.toString() ?? '';
    this.invoiceConsumptionClass = item.invoice_data.consumption_class?.toString() ?? '';
    this.nontaxableAmountInput = item.invoice_data.nontaxable_amount?.toString() ?? '';

    // Location inputs
    this.streetInput = item.location.street ?? '';
    this.numberInput = item.location.number ?? '';
    this.postalCodeInput = item.location.postal_code ?? '';
    this.cityInput = item.location.city ?? '';
    this.provinceInput = item.location.province ?? '';
    this.latitudeInput = item.location.latitude?.toString() ?? '';
    this.longitudeInput = item.location.longitude?.toString() ?? '';

    this.editedPDRs = item.pdr_data.map(pdr => ({
      // core gas fields
      spread: pdr.spread?.toString() || '',
      gas_market_price: pdr.gas_market_price?.toString() || '',
      price_gas_var_actual: pdr.price_gas_var_actual?.toString() || '',
      price_gas_fix: pdr.price_gas_fix?.toString() || '',
      hcv_gas: pdr.hcv_gas?.toString() || '',
      taxrate_gas_scale1: pdr.taxrate_gas_scale1?.toString() || '',
      taxrate_gas_scale2: pdr.taxrate_gas_scale2?.toString() || '',
      taxrate_gas_scale3: pdr.taxrate_gas_scale3?.toString() || '',
      taxrate_gas_scale4: pdr.taxrate_gas_scale4?.toString() || '',

      month_gas_consumption: pdr.month_gas_consumption?.toString() || '',
      variable_gas_consumption: pdr.variable_gas_consumption?.toString() || '',
      fixed_gas_consumption: pdr.fixed_gas_consumption?.toString() || '',
      daily_gas_capacity: pdr.daily_gas_capacity?.toString() || '',

      price_type_gas: pdr.price_type_gas ?? undefined,
      selectedPriceTypeDropdown: this.priceTypeDropdownOptions
        .findIndex(opt => opt.toUpperCase() === (pdr.price_type_gas ?? '').toUpperCase()),

      gas_market_index: pdr.gas_market_index ?? '',
      gas_intensity_class: pdr.gas_intensity_class ?? '',
      gas_meter_class: pdr.gas_meter_class ?? '',
      REMI_type_gas: pdr.REMI_type_gas ?? '',
      area_gas: pdr.area_gas ?? '',
      tax_region: pdr.location?.postal_code ?? '',

      daily_capacity_factor_gas: pdr.daily_capacity_factor_gas?.toString() || '',
      fixed_charge_gas_actual: pdr.fixed_charge_gas_actual?.toString() || '',
      material_cost_factor: pdr.material_cost_factor?.toString() || '',
      material_costs_gas_variable_actual: pdr.material_costs_gas_variable_actual?.toString() || '',
      material_costs_gas_fixed_actual: pdr.material_costs_gas_fixed_actual?.toString() || '',
      fixed_distribution_costs_gas_actual: pdr.fixed_distribution_costs_gas_actual?.toString() || '',
      fixed_distribution_costs_gas_calc: pdr.fixed_distribution_costs_gas_calc?.toString() || '',
      variable_transportation_fee: pdr.variable_transportation_fee?.toString() || '',
      var_transportation_costs_actual: pdr.var_transportation_costs_actual?.toString() || '',
      total_var_distribution_costs_actual: pdr.total_var_distribution_costs_actual?.toString() || '',

      var_distribution_fee_tier1: pdr.var_distribution_fee_tier1?.toString() || '',
      var_distribution_fee_tier2: pdr.var_distribution_fee_tier2?.toString() || '',
      var_distribution_fee_tier3: pdr.var_distribution_fee_tier3?.toString() || '',
      var_distribution_fee_tier4: pdr.var_distribution_fee_tier4?.toString() || '',
      var_distribution_fee_tier5: pdr.var_distribution_fee_tier5?.toString() || '',
      var_distribution_fee_tier6: pdr.var_distribution_fee_tier6?.toString() || '',
      var_distribution_fee_tier7: pdr.var_distribution_fee_tier7?.toString() || '',
      var_distribution_fee_tier8: pdr.var_distribution_fee_tier8?.toString() || '',

      vat_rate: pdr.vat_rate?.toString() || '',
      vat_amount_actual: pdr.vat_amount_actual?.toString() || '',

      tms_gas_charges_actual: pdr.tms_gas_charges_actual?.toString() || '',

      total_gas_taxes_actual: pdr.total_gas_taxes_actual?.toString() || '',
      additional_gas_charges: pdr.additional_gas_charges?.toString() || '',
      total_gas_discount_amount: pdr.total_gas_discount_amount?.toString() || '',

      total_taxes: pdr.total_gas_taxes_actual?.toString() || '',
      fixed_quota: pdr.fixed_quota?.toString() || '',
      forecast_index_value: pdr.forecast_index_value?.toString() || '',
      gas_perc_with_var_price: pdr.gas_perc_with_var_price?.toString() || '',
      exercise_tax_type_gas: pdr.exercise_tax_type_gas || '',
      tax_type_gas: pdr.tax_type_gas || '',
      pdr_id: pdr.pdr_id || '',

      // location
      location: {
        street: pdr.location?.street ?? '',
        number: pdr.location?.number ?? '',
        postal_code: pdr.location?.postal_code ?? '',
        city: pdr.location?.city ?? '',
        province: pdr.location?.province ?? '',
      },

      // supplierData
      supplierData: {
        name: pdr.supplier?.name ?? '',
        vat_id: pdr.supplier?.vat_id ?? '',
        contact_name: pdr.supplier?.contact_name ?? '',
        contact_number: pdr.supplier?.contact_number ?? '',
        email: pdr.supplier?.email ?? '',
        start_of_supply: pdr.supplier?.start_of_supply
          ? new Date(pdr.supplier.start_of_supply)
          : undefined,
        end_of_supply: pdr.supplier?.end_of_supply
          ? new Date(pdr.supplier.end_of_supply)
          : undefined,
      }
    }));
  }

  changePdrCount(change: number): void {
    const newCount = this.numberOfPdrs + change;

    if (newCount < 0) {
      return;
    }

    this.numberOfPdrs = newCount;

    if (change === -1) {
      // Remove last pod if it exists
      if (this.editedPDRs.length > 0) {
        this.editedPDRs.pop();
      }
    } else if (change === 1) {
      // Add a new empty pod
      this.editedPDRs.push({
        gas_intensity_class: '',
        gas_meter_class: '',
        REMI_type_gas: '',
        area_gas: '',
        forecast_index_value: '',
        exercise_tax_type_gas: '',
        tax_type_gas: '',
        pdr_id: '',
        location: {
          street: '',
          number: '',
          postal_code: '',
          city: '',
          province: ''
        },
        supplierData: {
          name: '',
          vat_id: '',
          contact_name: '',
          contact_number: '',
          email: '',
          start_of_supply: undefined,
          end_of_supply: undefined
        }
      });
    }
  }

  copyText(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.toast.showToast('shared.success', 'admin.invite_copied', 'success', 2000, true);
    }).catch(err => {
    });
  }

  copyJson() {
    const invoice = this.getGasInvoiceDataFromInputs();
    const json = JSON.stringify(invoice, null, 2);
    this.copyText(json);
  }

  downloadJson() {
    const invoice = this.getGasInvoiceDataFromInputs();
    const json = JSON.stringify(invoice, null, 2);

    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    
    const downloadedFileName = this.recordData?.file_name || `gas_invoice-${new Date().toISOString()}.json`;
    a.download = downloadedFileName;

    a.click();

    URL.revokeObjectURL(url);
  }

  resetDialogData() {
    this.numberOfPdrs = 0;

    this.invoiceNumberInput =
      this.totalConsumptionInput =
      this.invoiceAmountActualInput =
      this.invoiceAmountGrossInput =
      this.vatRateInput =
      this.vatAmountInput =
      this.nontaxableAmountInput =
      this.invoiceIdInput =
      this.invoiceFileNameInput =
      this.streetInput =
      this.numberInput =
      this.postalCodeInput =
      this.cityInput =
      this.provinceInput =
      this.latitudeInput =
      this.longitudeInput =
      this.invoiceConsumptionClass = '';

    this.forPeriodDate = undefined;

    this.editedPDRs = [];

    this.recordData = null;
  }

  async saveJson() {
    const invoiceData = this.getGasInvoiceDataFromInputs();

    const newData: JSONGeneratorHistoryGasCreate = {
      file_name: this.invoiceFileNameInput || '',
      json_history: invoiceData
    }

    try {
      if (this.recordData == null) {
        await lastValueFrom(this.adminService.createGasJsonRecord(newData));
      } else {
        await lastValueFrom(this.adminService.updateGasJsonRecord(this.recordData.id, newData));
      }
    }
    catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 8000, false);
      return;
    }

    if (this.reloadCallbackFunction) {
      this.reloadCallbackFunction();
    }
    this.closeModal();
  }
}
